import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Loader2, Crown, LogOut, Upload, CheckCircle2, FileText } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'member_portal_token';
const fmtAUD = (n) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n || 0);
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

const authFetch = (path, opts = {}) => fetch(`${BACKEND_URL}${path}`, {
  ...opts,
  headers: { ...(opts.headers || {}), Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ''}` },
});

export default function MemberPortal() {
  const [me, setMe] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    (async () => {
      if (!localStorage.getItem(TOKEN_KEY)) { setChecking(false); return; }
      try {
        const r = await authFetch('/api/member-portal/me');
        if (r.ok) setMe(await r.json());
        else localStorage.removeItem(TOKEN_KEY);
      } catch { /* ignore */ }
      setChecking(false);
    })();
  }, []);

  if (checking) {
    return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><Loader2 className="h-8 w-8 text-emerald-400 animate-spin" /></div>;
  }
  return me
    ? <PortalDashboard me={me} onLogout={() => { localStorage.removeItem(TOKEN_KEY); setMe(null); }} />
    : <PortalLogin onLogin={setMe} />;
}

function PortalLogin({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/member-portal/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'Login failed');
      localStorage.setItem(TOKEN_KEY, d.token);
      const meRes = await authFetch('/api/member-portal/me');
      onLogin(await meRes.json());
    } catch (err) { setError(err.message); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4" data-testid="member-portal-login">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 mb-3">
            <Crown className="h-7 w-7 text-emerald-400" />
          </div>
          <h1 className="text-white text-2xl font-bold font-['Chivo']">Member Portal</h1>
          <p className="text-slate-400 text-sm mt-1">View your membership balance, invoices & upload payment receipts</p>
        </div>
        <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl p-6 space-y-4">
          <div>
            <Label>Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="portal-email-input" />
          </div>
          <div>
            <Label>Password</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="portal-password-input" />
          </div>
          {error && <p className="text-sm text-rose-600" data-testid="portal-login-error">{error}</p>}
          <Button type="submit" disabled={submitting} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 rounded-xl font-semibold" data-testid="portal-login-btn">
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Log in
          </Button>
          <p className="text-[11px] text-slate-400 text-center">Don't have a login? Contact the workshop to set one up.</p>
        </form>
      </div>
    </div>
  );
}

const PayBadge = ({ status }) => {
  const map = {
    paid: ['bg-emerald-100 text-emerald-700', 'Paid'],
    receipt_uploaded: ['bg-blue-100 text-blue-700', 'Receipt uploaded — awaiting confirmation'],
    unpaid: ['bg-rose-100 text-rose-700', 'Unpaid'],
  };
  const [cls, label] = map[status] || ['bg-slate-100 text-slate-600', status];
  return <Badge className={`${cls} hover:${cls}`}>{label}</Badge>;
};

function PortalDashboard({ me, onLogout }) {
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(me);
  const [uploadingId, setUploadingId] = useState(null);
  const fileInputRef = useRef(null);
  const pendingPaymentRef = useRef(null);

  const refresh = async () => {
    try {
      const [pRes, iRes, mRes] = await Promise.all([
        authFetch('/api/member-portal/payments'),
        authFetch('/api/member-portal/invoices'),
        authFetch('/api/member-portal/me'),
      ]);
      if (pRes.ok) setPayments(await pRes.json());
      if (iRes.ok) setInvoices(await iRes.json());
      if (mRes.ok) setSummary(await mRes.json());
    } catch { /* ignore */ }
  };
   
  useEffect(() => { refresh(); }, []);

  const pickReceipt = (paymentId) => {
    pendingPaymentRef.current = paymentId;
    fileInputRef.current?.click();
  };

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const paymentId = pendingPaymentRef.current;
    if (!file || !paymentId) return;
    setUploadingId(paymentId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await authFetch(`/api/member-portal/payments/${paymentId}/receipt`, { method: 'POST', body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'Upload failed');
      toast.success(d.message || 'Receipt uploaded');
      refresh();
    } catch (err) { toast.error(err.message); }
    finally { setUploadingId(null); pendingPaymentRef.current = null; }
  };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="member-portal-dashboard">
      <input ref={fileInputRef} type="file" accept=".jpg,.jpeg,.png,.webp,.heic,.pdf" className="hidden" onChange={onFileChosen} data-testid="receipt-file-input" />
      <header className="bg-slate-950 px-4 sm:px-8 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
              <Crown className="h-5 w-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-white font-semibold text-sm sm:text-base">{summary.company_name || `${summary.first_name} ${summary.last_name || ''}`}</p>
              <p className="text-slate-400 text-xs">{summary.site_name} · Member since {fmtDate(summary.member_since)}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-slate-800" onClick={onLogout} data-testid="portal-logout-btn">
            <LogOut className="h-4 w-4 mr-1" /> Log out
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-8 py-6 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Monthly subscription" value={fmtAUD(summary.monthly_amount)} testid="portal-kpi-monthly" />
          <StatCard label="Accumulated" value={fmtAUD(summary.accumulated)} tone="text-emerald-600" testid="portal-kpi-accumulated" />
          <StatCard label="Used on services" value={fmtAUD(summary.used)} testid="portal-kpi-used" />
          <StatCard label="Balance" value={fmtAUD(summary.balance)} tone={summary.balance < 0 ? 'text-rose-600' : 'text-blue-600'} testid="portal-kpi-balance" />
        </div>

        <section className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="portal-subscriptions">
          <h2 className="font-semibold text-slate-900 mb-4">Monthly Subscriptions</h2>
          {payments.length === 0 ? (
            <p className="text-sm text-slate-500 py-3">No subscription months yet — you'll get an email when your first month is due.</p>
          ) : (
            <div className="space-y-3">
              {payments.map((p) => (
                <div key={p.payment_id} className="flex flex-wrap items-center justify-between gap-3 border border-slate-100 rounded-xl px-4 py-3" data-testid={`portal-payment-${p.period}`}>
                  <div>
                    <p className="font-medium text-slate-900 text-sm">{p.period_label || p.period}</p>
                    <p className="text-xs text-slate-500">{fmtAUD(p.amount_due)}</p>
                    {p.rejected_reason && p.status === 'unpaid' && (
                      <p className="text-xs text-rose-500 mt-0.5">Previous receipt rejected — please upload again</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <PayBadge status={p.status} />
                    {p.status !== 'paid' && (
                      <Button size="sm" variant="outline" className="h-9" disabled={uploadingId === p.payment_id}
                        onClick={() => pickReceipt(p.payment_id)} data-testid={`upload-receipt-${p.period}`}>
                        {uploadingId === p.payment_id
                          ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                          : <Upload className="h-4 w-4 mr-1" />}
                        {p.status === 'receipt_uploaded' ? 'Replace receipt' : 'Upload receipt'}
                      </Button>
                    )}
                    {p.status === 'paid' && <CheckCircle2 className="h-5 w-5 text-emerald-500" />}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="portal-invoices">
          <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <FileText className="h-4 w-4 text-slate-400" /> Your Invoices
          </h2>
          {invoices.length === 0 ? (
            <p className="text-sm text-slate-500 py-3">No invoices yet.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {invoices.map((inv) => (
                <div key={inv.invoice_id} className="flex items-center justify-between py-3" data-testid="portal-invoice-row">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{inv.doc_no || inv.invoice_id}</p>
                    <p className="text-xs text-slate-500">
                      {fmtDate(inv.invoice_date || inv.created_at)}
                      {inv.vehicle_rego ? ` · ${inv.vehicle_rego}` : ''}
                      {inv.vehicle_make ? ` ${inv.vehicle_make} ${inv.vehicle_model || ''}` : ''}
                    </p>
                  </div>
                  <p className="font-semibold text-slate-900">{fmtAUD(inv.total)}</p>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function StatCard({ label, value, tone = 'text-slate-900', testid }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={testid}>
      <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`text-lg sm:text-xl font-semibold mt-1 ${tone}`}>{value}</p>
    </div>
  );
}
